import React, { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search, Download, ExternalLink, RotateCcw, Plus, Trash2, RefreshCw, Upload, X
} from 'lucide-react';
import {
  listPageVersions, createPageVersion, setPageVersion, deletePageVersion,
  rotatePageVersion, quickSwitch, getPageVersionRedirects,
  PageVersionView, SUPPORTED_LOCALES
} from '../api/pageVersions';
import PageHeader from '../components/shared/PageHeader';
import { Skel } from '../components/shared/Skeleton';

type Domain = 'onlyseeker' | 'osr' | 'o2s';
const DOMAINS: { value: Domain; label: string }[] = [
  { value: 'onlyseeker', label: 'OnlySeeker' },
  { value: 'osr', label: 'OSR' },
  { value: 'o2s', label: 'O2S' }
];

function toast(msg: string, type: 'success' | 'error' = 'success') {
  const el = document.createElement('div');
  el.textContent = msg;
  el.style.cssText = `position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:${type === 'success' ? 'var(--success)' : 'var(--danger)'};color:#fff;padding:8px 14px;border-radius:8px;z-index:1000;box-shadow:var(--shadow-lg);font-size:13px`;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2400);
}

// Reverse map: public origin → admin domain key.
const ORIGIN_TO_DOMAIN: Record<string, Domain> = {
  'onlyseeker.io':   'onlyseeker',
  'onlysearcher.io': 'osr',
  'only2search.com': 'o2s'
};
const ALLOWED_LOCALES: Set<string> = new Set(SUPPORTED_LOCALES.map(l => l.code as string));
const CREATOR_PAGE_VERSION_PREFIX = 'creator:';

function creatorPageVersionKey(username: string): string {
  return `${CREATOR_PAGE_VERSION_PREFIX}${username.trim().toLowerCase()}`;
}

function creatorUsernameFromPageVersionKey(slug: string): string | null {
  if (!slug.startsWith(CREATOR_PAGE_VERSION_PREFIX)) return null;
  const username = slug.slice(CREATOR_PAGE_VERSION_PREFIX.length).trim();
  return username || null;
}

interface ParsedUrl {
  ok: true;
  domain: Domain;
  locale: string;
  slug: string;
}
interface ParseError {
  ok: false;
  reason: string;
}

function parseLiveUrl(input: string): ParsedUrl | ParseError {
  const raw = input.trim();
  if (!raw) return { ok: false, reason: 'Empty input.' };
  let u: URL;
  try {
    u = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
  } catch {
    return { ok: false, reason: 'Not a valid URL.' };
  }
  const host = u.hostname.replace(/^www\./, '');
  const domain = ORIGIN_TO_DOMAIN[host];
  if (!domain) return { ok: false, reason: `Unknown host "${host}". Allowed: ${Object.keys(ORIGIN_TO_DOMAIN).join(', ')}.` };

  const segments = u.pathname.split('/').filter(Boolean);
  let locale = 'en';
  let i = 0;
  if (segments.length > 0 && ALLOWED_LOCALES.has(segments[0])) {
    locale = segments[0];
    i++;
  }
  const seg = segments[i];

  // Creator profile: /creators/{username}-onlyfans[-N]. The namespace keeps
  // this page-version entry distinct from an equally named catalog tag.
  if (seg === 'creators') {
    const profile = segments[i + 1];
    if (!profile || segments.length !== i + 2) {
      return { ok: false, reason: 'Creator URL must be /creators/<username>-onlyfans.' };
    }
    const match = profile.match(/^(.+)-onlyfans(?:-(?:v)?\d+)?$/);
    const username = match?.[1]?.trim().toLowerCase();
    if (!username) return { ok: false, reason: 'Creator username is empty.' };
    return { ok: true, domain, locale, slug: creatorPageVersionKey(username) };
  }

  const expectedSeg = locale === 'de' ? 'beste' : 'best';
  // Accept either bare `/best[-N]` (index) or `/best/<numbered>` legacy form
  // by stripping trailing `-N` from the segment first.
  if (seg && /^(best|beste)(?:-(?:v)?\d+)?$/.test(seg)) {
    if (seg !== expectedSeg && !seg.startsWith(expectedSeg + '-')) {
      return { ok: false, reason: `Expected "${expectedSeg}" for locale ${locale}, got "${seg}".` };
    }
  } else if (seg !== expectedSeg) {
    return { ok: false, reason: `Expected "/${expectedSeg}/…", got "/${seg ?? ''}".` };
  }
  i++;
  const tail = segments[i];

  // Index page: URL ends here (e.g., /best, /es/best, /de/beste, /best-3)
  if (!tail) {
    return { ok: true, domain, locale, slug: 'best' };
  }

  // Tag page: must be /onlyfans-<slug>
  if (!tail.startsWith('onlyfans-')) {
    return { ok: false, reason: `Path tail must be "onlyfans-<slug>" or omitted (for index). Got "${tail}".` };
  }
  let slug = tail.slice('onlyfans-'.length);
  slug = slug.replace(/-(?:v)?\d+$/, '');
  if (!slug) return { ok: false, reason: 'Slug part is empty.' };
  if (slug === 'best') return { ok: false, reason: '"onlyfans-best" conflicts with the index page slug. Pick a different tag.' };
  return { ok: true, domain, locale, slug };
}

interface BulkInput {
  lineNumber: number;
  source: string;
  slug: string;
  inputVersion: number;
}

interface BulkParseError {
  reason: string;
}

interface BulkPlanItem extends BulkInput {
  action: 'rotate' | 'create';
  from: number;
  to: number;
  oldUrl: string;
  newUrl: string;
}

interface BulkResult extends BulkPlanItem {
  ok: boolean;
  error?: string;
}

function isPlainPageVersionKey(value: string): boolean {
  return value === 'best'
    || /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(value)
    || /^creator:[a-z0-9][a-z0-9._-]*$/i.test(value);
}

function extractVersionFromPath(value: string): number {
  let path = value;
  try {
    const url = value.startsWith('/')
      ? `https://onlyseeker.io${value}`
      : /^https?:\/\//i.test(value) ? value : `https://${value}`;
    path = new URL(url).pathname;
  } catch {
    return 0;
  }
  const last = path.split('/').filter(Boolean).at(-1) ?? '';
  const match = last.match(/(?:-onlyfans|best|beste|onlyfans-.+)-(?:v)?(\d+)$/);
  return match?.[1] ? Number.parseInt(match[1], 10) : 0;
}

function parseBulkInput(value: string, lineNumber: number, targetDomain: Domain, targetLocale: string): BulkInput | BulkParseError {
  const source = value.trim();
  if (!source) return { reason: `Line ${lineNumber}: empty value.` };

  if (isPlainPageVersionKey(source)) {
    const creatorUsername = creatorUsernameFromPageVersionKey(source);
    return {
      lineNumber,
      source,
      slug: creatorUsername ? creatorPageVersionKey(creatorUsername) : source.toLowerCase(),
      inputVersion: 0,
    };
  }

  const parsed = parseLiveUrl(source.startsWith('/') ? `${PUBLIC_ORIGIN[targetDomain]}${source}` : source);
  if (!parsed.ok) return { reason: `Line ${lineNumber}: ${parsed.reason}` };
  if (parsed.domain !== targetDomain || parsed.locale !== targetLocale) {
    return {
      reason: `Line ${lineNumber}: URL belongs to ${parsed.domain.toUpperCase()} / ${parsed.locale.toUpperCase()}, but the selected scope is ${targetDomain.toUpperCase()} / ${targetLocale.toUpperCase()}.`,
    };
  }

  return {
    lineNumber,
    source,
    slug: parsed.slug,
    inputVersion: extractVersionFromPath(source),
  };
}

function makeBulkPlan(inputs: BulkInput[], existing: PageVersionView[], domain: Domain, locale: string): { plan: BulkPlanItem[]; warnings: string[] } {
  const existingBySlug = new Map(existing.map(item => [item.tagSlug, item]));
  const seen = new Map<string, BulkInput>();
  const warnings: string[] = [];
  const plan: BulkPlanItem[] = [];

  for (const item of inputs) {
    const first = seen.get(item.slug);
    if (first) {
      warnings.push(`Line ${item.lineNumber}: duplicate "${item.slug}" (already on line ${first.lineNumber}) was skipped.`);
      continue;
    }
    seen.set(item.slug, item);

    const current = existingBySlug.get(item.slug);
    const from = current?.currentNumber ?? item.inputVersion;
    const to = current ? current.maxNumber + 1 : from + 1;
    plan.push({
      ...item,
      action: current ? 'rotate' : 'create',
      from,
      to,
      oldUrl: buildLiveUrl(item.slug, locale, from, domain),
      newUrl: buildLiveUrl(item.slug, locale, to, domain),
    });
  }

  return { plan, warnings };
}

const PageVersionsPage: React.FC = () => {
  const qc = useQueryClient();
  const [domain, setDomain] = useState<Domain>('onlyseeker');
  const [locale, setLocale] = useState<string>('en');
  const [search, setSearch] = useState('');
  const [newSlugOpen, setNewSlugOpen] = useState(false);
  const [newSlugInput, setNewSlugInput] = useState('');
  const [newSlugBusy, setNewSlugBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkPlan, setBulkPlan] = useState<BulkPlanItem[]>([]);
  const [bulkWarnings, setBulkWarnings] = useState<string[]>([]);
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);
  const [bulkResults, setBulkResults] = useState<BulkResult[]>([]);
  const [bulkAllowCreate, setBulkAllowCreate] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkScope, setBulkScope] = useState<{ domain: Domain; locale: string } | null>(null);
  const bulkFileRef = useRef<HTMLInputElement>(null);

  const { data: versions, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['page-versions', domain, locale],
    queryFn: () => listPageVersions(domain, locale)
  });

  const { data: redirects } = useQuery({
    queryKey: ['page-versions', 'redirects', domain, locale],
    queryFn: () => getPageVersionRedirects(domain, locale)
  });

  const filtered = useMemo(() => {
    if (!versions) return [];
    const q = search.trim().toLowerCase();
    return q ? versions.filter(v => v.tagSlug.toLowerCase().includes(q)) : versions;
  }, [versions, search]);

  const redirectCount = useMemo(() => {
    if (!redirects) return new Map<string, number>();
    const m = new Map<string, number>();
    for (const src of Object.keys(redirects)) {
      // src can be:
      //   /best/onlyfans-<slug>[-N]          (tag page, EN)
      //   /<locale>/best|beste/onlyfans-<slug>[-N]
      //   /best[-N]                          (index page, EN; tagSlug === "best")
      //   /<locale>/best|beste[-N]           (index page, non-EN)
      const lastSeg = src.split('/').pop() ?? src;
      let bare: string;
      const segments = src.split('/').filter(Boolean);
      const creatorIndex = segments.indexOf('creators');
      const creatorMatch = creatorIndex >= 0 ? segments[creatorIndex + 1]?.match(/^(.+)-onlyfans(?:-(?:v)?\d+)?$/) : null;
      if (creatorMatch?.[1]) {
        bare = creatorPageVersionKey(creatorMatch[1]);
      } else if (lastSeg.startsWith('onlyfans-')) {
        bare = lastSeg.slice('onlyfans-'.length).replace(/-(?:v)?\d+$/, '');
      } else if (/^(best|beste)(?:-(?:v)?\d+)?$/.test(lastSeg)) {
        bare = 'best';
      } else {
        bare = lastSeg.replace(/-(?:v)?\d+$/, '');
      }
      if (!bare) continue;
      m.set(bare, (m.get(bare) ?? 0) + 1);
    }
    return m;
  }, [redirects]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['page-versions', domain, locale] });
    qc.invalidateQueries({ queryKey: ['page-versions', 'redirects', domain, locale] });
  };

  const createMut = useMutation({
    mutationFn: (slug: string) => createPageVersion({ tagSlug: slug, currentNumber: 0, domain, locale }),
    onSuccess: () => { toast('New version created'); refresh(); },
    onError: () => toast('Create failed', 'error')
  });

  const setMut = useMutation({
    mutationFn: ({ slug, n }: { slug: string; n: number }) => setPageVersion(slug, n, domain, locale),
    onSuccess: () => { toast('Live version updated'); refresh(); },
    onError: () => toast('Update failed', 'error')
  });

  const rotateMut = useMutation({
    mutationFn: (slug: string) => rotatePageVersion(slug, domain, locale),
    onSuccess: () => { toast('Rotated to next version'); refresh(); },
    onError: () => toast('Rotation failed', 'error')
  });

  const switchMut = useMutation({
    mutationFn: ({ slug, n }: { slug: string; n: number }) => quickSwitch(slug, n, domain, locale),
    onSuccess: () => { toast('Switched'); refresh(); },
    onError: () => toast('Switch failed', 'error')
  });

  const deleteMut = useMutation({
    mutationFn: (slug: string) => deletePageVersion(slug, domain, locale),
    onSuccess: () => { toast('Versions removed'); refresh(); },
    onError: () => toast('Delete failed', 'error')
  });

  const openNewSlug = () => {
    setNewSlugInput('');
    setNewSlugOpen(true);
  };

  const closeBulk = () => {
    if (bulkBusy) return;
    setBulkOpen(false);
    setBulkFileName('');
    setBulkPlan([]);
    setBulkWarnings([]);
    setBulkErrors([]);
    setBulkResults([]);
    setBulkAllowCreate(false);
    setBulkScope(null);
    if (bulkFileRef.current) bulkFileRef.current.value = '';
  };

  const openBulk = () => {
    closeBulk();
    setBulkOpen(true);
  };

  const loadBulkFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.txt') && file.type !== 'text/plain') {
      toast('Only .txt files are accepted', 'error');
      return;
    }
    if (!versions) {
      toast('Page versions are still loading. Refresh and try again.', 'error');
      return;
    }

    try {
      const lines = (await file.text()).replace(/^\uFEFF/, '').split(/\r?\n/);
      const inputs: BulkInput[] = [];
      const errors: string[] = [];
      for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index].trim();
        if (!line || line.startsWith('#')) continue;
        const parsedLine = parseBulkInput(line, index + 1, domain, locale);
        if ('reason' in parsedLine) errors.push(parsedLine.reason);
        else inputs.push(parsedLine);
      }
      const { plan, warnings } = makeBulkPlan(inputs, versions, domain, locale);
      setBulkFileName(file.name);
      setBulkPlan(plan);
      setBulkWarnings(warnings);
      setBulkErrors(errors);
      setBulkResults([]);
      setBulkScope({ domain, locale });
      setBulkAllowCreate(false);
      if (bulkFileRef.current) bulkFileRef.current.value = '';
    } catch {
      toast('Failed to read the text file', 'error');
    }
  };

  const missingBulkEntries = bulkPlan.filter(item => item.action === 'create');
  const executableBulkPlan = bulkPlan.filter(item => item.action === 'rotate' || bulkAllowCreate);

  const executeBulkPlan = async () => {
    if (!bulkScope || bulkErrors.length > 0 || bulkPlan.length === 0) return;
    if (bulkScope.domain !== domain || bulkScope.locale !== locale) {
      toast('The selected domain or locale changed. Upload the file again.', 'error');
      return;
    }
    if (missingBulkEntries.length > 0 && !bulkAllowCreate) {
      toast(`Enable “Create missing entries” for ${missingBulkEntries.length} missing page(s).`, 'error');
      return;
    }
    if (!window.confirm(`Create the next version for ${executableBulkPlan.length} page(s)? This changes their canonical URLs.`)) return;

    setBulkBusy(true);
    const results: BulkResult[] = [];
    let nextIndex = 0;
    const worker = async () => {
      while (nextIndex < executableBulkPlan.length) {
        const index = nextIndex;
        nextIndex += 1;
        const item = executableBulkPlan[index];
        try {
          let updated: PageVersionView;
          if (item.action === 'rotate') {
            updated = await rotatePageVersion(item.slug, bulkScope.domain, bulkScope.locale);
          } else {
            updated = await createPageVersion({
              tagSlug: item.slug,
              currentNumber: item.to,
              domain: bulkScope.domain,
              locale: bulkScope.locale,
            });
          }
          results.push({
            ...item,
            to: updated.currentNumber,
            newUrl: buildLiveUrl(item.slug, bulkScope.locale, updated.currentNumber, bulkScope.domain),
            ok: true,
          });
        } catch (error) {
          results.push({ ...item, ok: false, error: error instanceof Error ? error.message : 'Request failed' });
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(4, executableBulkPlan.length) }, worker));
    results.sort((a, b) => a.lineNumber - b.lineNumber);
    setBulkResults(results);
    setBulkBusy(false);
    refresh();
    const failed = results.filter(item => !item.ok).length;
    toast(failed ? `Completed with ${failed} failure(s)` : `Created ${results.length} next version(s)`, failed ? 'error' : 'success');
  };

  const downloadUrlsAfter = () => {
    if (bulkResults.length === 0 || bulkResults.some(item => !item.ok)) return;
    const body = `${bulkResults.map(item => item.newUrl).join('\n')}\n`;
    const href = URL.createObjectURL(new Blob([body], { type: 'text/plain;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = 'urls-after.txt';
    anchor.click();
    URL.revokeObjectURL(href);
  };

  const parsed = useMemo(() => parseLiveUrl(newSlugInput), [newSlugInput]);
  const existing = parsed.ok ? versions?.find(v => v.tagSlug === parsed.slug && v.domain === parsed.domain && v.locale === parsed.locale) : null;

  const submitNewSlug = async () => {
    if (!parsed.ok) {
      window.alert(parsed.reason);
      return;
    }
    if (existing) {
      window.alert(`Slug "${parsed.slug}" already has versioning. Use "+ Create v…" on its row.`);
      // Switch view to the slug's domain/locale so user can find it
      setDomain(parsed.domain);
      setLocale(parsed.locale);
      setNewSlugOpen(false);
      return;
    }
    setNewSlugBusy(true);
    try {
      // Create entry and immediately make v1 the canonical version.
      // Backend behavior: first create auto-sets maxNumber=1; we explicitly set currentNumber=1.
      await createPageVersion({ tagSlug: parsed.slug, currentNumber: 0, domain: parsed.domain, locale: parsed.locale });
      await setPageVersion(parsed.slug, 1, parsed.domain, parsed.locale);
      toast('Versioning enabled — v1 is now canonical');
      // Switch view so user sees the new row right away
      setDomain(parsed.domain);
      setLocale(parsed.locale);
      setNewSlugOpen(false);
      refresh();
    } catch {
      toast('Create failed', 'error');
    } finally {
      setNewSlugBusy(false);
    }
  };

  const actions = (
    <>
      <button className="hf-btn" onClick={() => refetch()} disabled={isFetching}>
        <RefreshCw size={14} /> Refresh
      </button>
      <button className="hf-btn" onClick={openBulk}>
        <Upload size={14} /> Bulk TXT
      </button>
      <button className="hf-btn primary" onClick={openNewSlug}>
        <Plus size={14} /> New slug
      </button>
    </>
  );

  const previewV1Url = parsed.ok
    ? buildLiveUrl(parsed.slug, parsed.locale, 1, parsed.domain)
    : '';

  return (
    <>
      <PageHeader
        title="Page Versions"
        subtitle="One URL · multiple numbered variants for SEO."
        actions={actions}
      />

      <div className="hf-content">
        {/* Filter strip */}
        <div className="hf-toolbar">
          <div className="hf-tabs">
            {DOMAINS.map(d => (
              <button key={d.value} className={domain === d.value ? 'on' : ''} onClick={() => setDomain(d.value)}>
                {d.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {SUPPORTED_LOCALES.map(l => (
              <button
                key={l.code}
                type="button"
                className={`hf-chip ${locale === l.code ? 'on' : ''}`}
                onClick={() => setLocale(l.code)}
              >
                {l.code.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="hf-search" style={{ flex: '1 1 220px', marginLeft: 'auto' }}>
            <Search size={14} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="search slugs…" />
          </div>
        </div>

        {/* Body — list of slug rows */}
        {isLoading && (
          <>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="hf-card" aria-hidden="true">
                <div className="hf-card-body" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Skel width={36} height={36} style={{ borderRadius: 'var(--r-1)' }} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Skel width="40%" />
                    <Skel width="65%" className="sm" />
                  </div>
                  <Skel width={80} height={28} />
                </div>
              </div>
            ))}
          </>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="hf-card">
            <div className="hf-card-body" style={{ textAlign: 'center', color: 'var(--fg-3)' }}>
              No page versions for {domain.toUpperCase()} / {locale.toUpperCase()} yet.{' '}
              <button className="hf-btn primary sm" onClick={() => {
                const slug = window.prompt('Slug to start (e.g., blonde or creator:username):');
                if (slug) createMut.mutate(slug.trim());
              }}><Plus size={12} /> Create first slug</button>
            </div>
          </div>
        )}

        {filtered.map(v => (
          <PageVersionRow
            key={`${v.tagSlug}-${v.locale}-${v.domain}`}
            v={v}
            redirectsForSlug={redirectCount.get(v.tagSlug) ?? 0}
            onCreateNext={() => setMut.mutate({ slug: v.tagSlug, n: v.maxNumber + 1 })}
            onSetLive={(n) => setMut.mutate({ slug: v.tagSlug, n })}
            onRotate={() => rotateMut.mutate(v.tagSlug)}
            onSwitch={(n) => switchMut.mutate({ slug: v.tagSlug, n })}
            onDeleteAll={() => {
              if (window.confirm(`Delete ALL versions for /${v.tagSlug}?`)) deleteMut.mutate(v.tagSlug);
            }}
            pendingMutation={createMut.isPending || setMut.isPending || rotateMut.isPending || switchMut.isPending}
          />
        ))}
      </div>

      {/* ─── New slug modal — paste URL ─── */}
      {newSlugOpen && (
        <div className="hf-overlay" onClick={() => !newSlugBusy && setNewSlugOpen(false)}>
          <div className="hf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hf-modal-header">
              <h3 className="hf-modal-title">Enable versioning for a live page</h3>
              <button className="hf-btn icon ghost" onClick={() => setNewSlugOpen(false)} disabled={newSlugBusy}>
                <X size={14} />
              </button>
            </div>
            <div className="hf-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div className="hf-label-row">
                  <span className="hf-label">Paste the live URL</span>
                </div>
                <div className="hf-input">
                  <input
                    type="url"
                    autoFocus
                    value={newSlugInput}
                    onChange={(e) => setNewSlugInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !newSlugBusy && parsed.ok && !existing) { e.preventDefault(); submitNewSlug(); } }}
                    placeholder="https://onlyseeker.io/best/onlyfans-asmr-blowjob or /creators/jane-onlyfans"
                  />
                </div>
                <div className="hf-help">
                  Accepts any of <span className="hf-mono">onlyseeker.io</span>, <span className="hf-mono">onlysearcher.io</span>, <span className="hf-mono">only2search.com</span>.
                  Locale auto-detected from path.
                </div>
              </div>

              {newSlugInput.trim().length > 0 && (
                parsed.ok ? (
                  <div className="hf-feature-box muted" style={{ padding: 14 }}>
                    <div className="hf-label" style={{ marginBottom: 6 }}>Detected</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 10px', fontSize: 12.5 }}>
                      <span style={{ color: 'var(--fg-3)' }}>Domain</span>
                      <span className="hf-mono">{parsed.domain}</span>
                      <span style={{ color: 'var(--fg-3)' }}>Locale</span>
                      <span className="hf-mono">{parsed.locale}</span>
                      <span style={{ color: 'var(--fg-3)' }}>Slug</span>
                      <span className="hf-mono">{parsed.slug}</span>
                    </div>
                    {existing ? (
                      <div className="hf-banner warning" style={{ marginTop: 10 }}>
                        Already has versioning ({existing.maxNumber} version{existing.maxNumber === 1 ? '' : 's'}, currently {existing.currentNumber === 0 ? 'base' : `v${existing.currentNumber}`} live).
                        Will switch view to this slug — use <span className="hf-mono">+ Create v…</span> on its row.
                      </div>
                    ) : (
                      <div className="hf-help" style={{ marginTop: 10 }}>
                        On submit: v1 will be created and become canonical.
                        Base URL will 301 → <span className="hf-mono" style={{ wordBreak: 'break-all' }}>{previewV1Url}</span>.
                        You can then add v2/v3, rotate, rollback to base — all from the row that appears.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="hf-banner danger">{parsed.reason}</div>
                )
              )}
            </div>
            <div className="hf-modal-footer">
              <button className="hf-btn" onClick={() => setNewSlugOpen(false)} disabled={newSlugBusy}>Cancel</button>
              <button
                className="hf-btn primary"
                onClick={submitNewSlug}
                disabled={newSlugBusy || !parsed.ok || !!existing}
              >
                {newSlugBusy ? 'Creating…' : existing ? 'Open existing' : 'Enable versioning'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Bulk rotate/create from a text file ─── */}
      {bulkOpen && (
        <div className="hf-overlay" onClick={closeBulk}>
          <div className="hf-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="hf-modal-header">
              <h3 className="hf-modal-title">Bulk create next versions</h3>
              <button className="hf-btn icon ghost" onClick={closeBulk} disabled={bulkBusy}>
                <X size={14} />
              </button>
            </div>

            <div className="hf-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="hf-feature-box muted" style={{ padding: 14 }}>
                <div className="hf-label" style={{ marginBottom: 6 }}>Upload a .txt list</div>
                <div className="hf-help" style={{ marginTop: 0 }}>
                  One public URL or page-version key per line. Empty lines and <span className="hf-mono">#</span> comments are ignored.
                  The file must match the selected <span className="hf-mono">{domain.toUpperCase()} / {locale.toUpperCase()}</span> scope.
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <input
                    ref={bulkFileRef}
                    type="file"
                    accept=".txt,text/plain"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void loadBulkFile(file);
                    }}
                    style={{ display: 'none' }}
                  />
                  <button className="hf-btn" onClick={() => bulkFileRef.current?.click()} disabled={bulkBusy || isLoading}>
                    <Upload size={14} /> Choose .txt
                  </button>
                  {bulkFileName && <span className="hf-mono" style={{ fontSize: 12 }}>{bulkFileName}</span>}
                </div>
              </div>

              {bulkErrors.length > 0 && (
                <div className="hf-banner danger">
                  <strong>Nothing will be changed until every line is valid.</strong>
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                    {bulkErrors.slice(0, 12).map(error => <li key={error}>{error}</li>)}
                  </ul>
                  {bulkErrors.length > 12 && <div style={{ marginTop: 6 }}>…and {bulkErrors.length - 12} more invalid line(s).</div>}
                </div>
              )}

              {bulkPlan.length > 0 && (
                <>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span className="hf-tag outline sm">{bulkPlan.length} unique page{bulkPlan.length === 1 ? '' : 's'}</span>
                    <span className="hf-tag outline sm">{bulkPlan.filter(item => item.action === 'rotate').length} rotate</span>
                    {missingBulkEntries.length > 0 && <span className="hf-tag accent sm">{missingBulkEntries.length} missing</span>}
                  </div>

                  {missingBulkEntries.length > 0 && (
                    <label className="hf-feature-box muted" style={{ padding: 12, display: 'flex', alignItems: 'flex-start', gap: 9, cursor: bulkBusy ? 'default' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={bulkAllowCreate}
                        onChange={(event) => setBulkAllowCreate(event.target.checked)}
                        disabled={bulkBusy}
                        style={{ marginTop: 3 }}
                      />
                      <span className="hf-help" style={{ marginTop: 0 }}>
                        <strong>Create missing entries.</strong> Their source version plus one becomes canonical; without this explicit consent, missing pages are not mutated.
                      </span>
                    </label>
                  )}

                  {bulkWarnings.length > 0 && (
                    <div className="hf-banner warning">
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {bulkWarnings.slice(0, 8).map(warning => <li key={warning}>{warning}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="hf-card" style={{ maxHeight: 260, overflow: 'auto' }}>
                    <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 7, padding: 12 }}>
                      {bulkPlan.slice(0, 80).map(item => (
                        <div key={`${item.lineNumber}:${item.slug}`} style={{ display: 'grid', gridTemplateColumns: '52px minmax(120px, 1fr) minmax(150px, 1.4fr)', gap: 8, alignItems: 'baseline', fontSize: 12 }}>
                          <span className={`hf-tag ${item.action === 'rotate' ? 'outline' : 'accent'} sm`} style={{ justifySelf: 'start' }}>{item.action}</span>
                          <span className="hf-mono">{item.slug}</span>
                          <span className="hf-mono" style={{ color: 'var(--fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${item.oldUrl} → ${item.newUrl}`}>
                            v{item.from} → v{item.to}
                          </span>
                        </div>
                      ))}
                      {bulkPlan.length > 80 && <div className="hf-help">…and {bulkPlan.length - 80} more page(s).</div>}
                    </div>
                  </div>
                </>
              )}

              {bulkResults.length > 0 && (
                <div className={bulkResults.some(item => !item.ok) ? 'hf-banner warning' : 'hf-banner success'}>
                  {bulkResults.filter(item => item.ok).length} succeeded, {bulkResults.filter(item => !item.ok).length} failed.
                  {bulkResults.some(item => !item.ok) ? (
                    <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                      {bulkResults.filter(item => !item.ok).slice(0, 8).map(item => <li key={`${item.lineNumber}:${item.slug}`}>{item.slug}: {item.error}</li>)}
                    </ul>
                  ) : (
                    <button className="hf-btn sm" style={{ marginTop: 10 }} onClick={downloadUrlsAfter}>
                      <Download size={12} /> Download urls-after.txt
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="hf-modal-footer">
              <button className="hf-btn" onClick={closeBulk} disabled={bulkBusy}>Cancel</button>
              <button
                className="hf-btn primary"
                onClick={() => void executeBulkPlan()}
                disabled={bulkBusy || bulkPlan.length === 0 || bulkErrors.length > 0 || (missingBulkEntries.length > 0 && !bulkAllowCreate)}
              >
                {bulkBusy ? 'Creating…' : `Create next versions (${executableBulkPlan.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

interface RowProps {
  v: PageVersionView;
  redirectsForSlug: number;
  onCreateNext: () => void;
  onSetLive: (n: number) => void;
  onRotate: () => void;
  onSwitch: (n: number) => void;
  onDeleteAll: () => void;
  pendingMutation: boolean;
}

// Origin of the public site per admin domain key.
const PUBLIC_ORIGIN: Record<string, string> = {
  onlyseeker: 'https://onlyseeker.io',
  osr:        'https://onlysearcher.io',
  o2s:        'https://only2search.com'
};

// Compose live URL on the public site.
// Normal tag pages:
//   EN:     `<origin>/best/onlyfans-<slug>[-N]`
//   DE:     `<origin>/de/beste/onlyfans-<slug>[-N]`
//   other:  `<origin>/<locale>/best/onlyfans-<slug>[-N]`
// Index page (special: tagSlug === "best"):
//   EN:     `<origin>/best[-N]`
//   DE:     `<origin>/de/beste[-N]`
//   other:  `<origin>/<locale>/best[-N]`
function buildLiveUrl(slug: string, locale: string, currentNumber: number, domain: string): string {
  const origin = PUBLIC_ORIGIN[domain] ?? '';
  const seg = locale === 'de' ? 'beste' : 'best';
  const prefix = locale === 'en' ? '' : `/${locale}`;
  const suffix = currentNumber > 0 ? `-${currentNumber}` : '';
  const creatorUsername = creatorUsernameFromPageVersionKey(slug);
  if (creatorUsername) {
    return `${origin}${prefix}/creators/${creatorUsername}-onlyfans${suffix}`;
  }
  if (slug === 'best') {
    return `${origin}${prefix}/${seg}${suffix}`;
  }
  return `${origin}${prefix}/${seg}/onlyfans-${slug}${suffix}`;
}

const PageVersionRow: React.FC<RowProps> = ({
  v, redirectsForSlug, onCreateNext, onSetLive, onRotate, onSwitch, onDeleteAll, pendingMutation
}) => {
  const numbers = useMemo(() => {
    const arr: number[] = [];
    for (let i = 1; i <= v.maxNumber; i++) arr.push(i);
    return arr;
  }, [v.maxNumber]);

  const recovered = new Set(v.recoveredNumbers ?? []);
  const isBaseLive = v.currentNumber === 0;
  const creatorUsername = creatorUsernameFromPageVersionKey(v.tagSlug);
  const liveUrl = buildLiveUrl(v.tagSlug, v.locale, v.currentNumber, v.domain);
  // Display the path portion only (cleaner UI; full origin only used in the link).
  const livePath = liveUrl.replace(/^https?:\/\/[^/]+/, '');

  return (
    <div className="hf-card">
      <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em' }}>
              {creatorUsername ? `Creator: ${creatorUsername}` : `/${v.tagSlug}`}
            </h3>
            <span className="hf-tag outline sm">{v.maxNumber} version{v.maxNumber === 1 ? '' : 's'}</span>
            {isBaseLive && <span className="hf-tag accent sm">base live</span>}
          </div>
          <span className="hf-mono" style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>
            {livePath}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <button className="hf-btn sm" onClick={onRotate} disabled={pendingMutation} title="Rotate to next version">
              <RotateCcw size={12} /> Rotate
            </button>
            <button className="hf-btn primary sm" onClick={onCreateNext} disabled={pendingMutation}>
              <Plus size={12} /> Create v{v.maxNumber + 1}
            </button>
            <button className="hf-btn ghost sm" onClick={onDeleteAll} title="Delete all versions for this slug">
              <Trash2 size={12} />
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* BASE pill (currentNumber = 0) — no version, canonical URL */}
            <div className="hf-vp">
              <button
                className={`hf-vp-square ${isBaseLive ? 'live' : ''}`}
                onClick={() => isBaseLive ? null : onSetLive(0)}
                disabled={pendingMutation}
                title={isBaseLive ? 'Base URL is live (no version active)' : 'Switch back to base (no version)'}
                style={{ fontSize: 11, fontFamily: 'var(--font-sans)', fontWeight: 600, letterSpacing: '-0.01em' }}
              >
                base
              </button>
              <span className={`hf-vp-label ${isBaseLive ? 'live' : ''}`}>
                {isBaseLive ? 'live' : 'off'}
              </span>
            </div>

            {numbers.map(n => {
              const isLive = !isBaseLive && n === v.currentNumber;
              const isDraft = recovered.has(n);
              const cls = isLive ? 'live' : isDraft ? 'draft' : '';
              return (
                <div key={n} className="hf-vp">
                  <button
                    className={`hf-vp-square ${cls}`}
                    onClick={() => isLive ? null : onSetLive(n)}
                    disabled={pendingMutation}
                    title={isLive ? 'Current live version' : `Set v${n} as live`}
                  >
                    {n}
                  </button>
                  <span className={`hf-vp-label ${isLive ? 'live' : ''}`}>
                    {isLive ? 'live' : isDraft ? 'draft' : 'past'}
                  </span>
                </div>
              );
            })}
            <div className="hf-vp">
              <button className="hf-vp-square add" onClick={onCreateNext} disabled={pendingMutation} title="Create next version">+</button>
              <span className="hf-vp-label">new</span>
            </div>
          </div>

          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div className="hf-help" style={{ marginTop: 0, marginBottom: 2 }}>redirects in</div>
            <div className="hf-mono" style={{ fontSize: 22, fontWeight: 600, color: redirectsForSlug > 0 ? 'var(--accent)' : 'var(--fg-3)', letterSpacing: '-0.02em' }}>
              {redirectsForSlug}
            </div>
          </div>
        </div>

        <div className="hf-divider dashed" />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span className="hf-help" style={{ marginTop: 0 }}>
            <span className="hf-mono">base</span> = no version active (canonical URL).
            Click any past version pill to swap live.
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <a
              href={liveUrl}
              target="_blank" rel="noreferrer noopener"
              className="hf-btn ghost sm"
            >
              <ExternalLink size={12} /> Open live
            </a>
            {v.currentNumber > 0 && (
              <button
                className="hf-btn ghost sm"
                onClick={() => onSetLive(0)}
                disabled={pendingMutation}
                title="Deactivate versioning — base URL becomes canonical"
              >
                <RotateCcw size={12} /> Back to base
              </button>
            )}
            {v.currentNumber > 1 && (
              <button className="hf-btn ghost sm" onClick={() => onSwitch(v.currentNumber - 1)} disabled={pendingMutation}>
                <RotateCcw size={12} /> v{v.currentNumber - 1}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageVersionsPage;
